import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Users, Activity, Coins, Target, Pencil, Twitter, MessageCircle, Send } from "lucide-react";
import { getHub, hubImageUrl, joinHub, leaveHub } from "@/api";
import { useApp } from "@/App";
import { useHubSession, errMsg } from "@/hooks/useHubSession";
import { HubAvatar, TierChip } from "@/components/hubs/HubCard";
import { PostItem } from "@/components/hubs/PostItem";
import { NewPost } from "@/components/hubs/NewPost";
import { CopyButton } from "@/components/CopyButton";

const LINK_ICON = { x: Twitter, discord: MessageCircle, telegram: Send };

function Stat({ icon: Icon, label, value, tid }) {
  return <div className="card-tile py-4"><div className="text-xs text-slate-500 flex items-center gap-1"><Icon size={13} /> {label}</div><div className="font-mono text-xl font-bold mt-1" data-testid={tid}>{value}</div></div>;
}

export default function HubPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const { showToast } = useApp();
  const { address, withSession } = useHubSession();
  const [h, setH] = useState(null);
  const [sort, setSort] = useState("new");
  const [busy, setBusy] = useState(false);
  const invite = params.get("invite") || "";

  const load = useCallback(() => getHub(slug, address || "", sort).then(setH).catch(() => setH(false)), [slug, address, sort]);
  useEffect(() => { load(); }, [load]);

  const toggleJoin = async () => {
    if (!address) return showToast("Connect your wallet to join", "error");
    setBusy(true);
    try { await withSession((t) => (h.joined ? leaveHub(t, slug) : joinHub(t, slug, invite))); await load(); }
    catch (e) { showToast(errMsg(e), "error"); }
    setBusy(false);
  };

  if (h === null) return <div className="pt-32 text-center text-slate-400">Loading…</div>;
  if (h === false) return <div className="pt-32 text-center text-slate-400" data-testid="hub-not-found">Hub not found. <Link to="/hubs" className="text-orange-300 underline">Browse hubs</Link></div>;
  const canAct = !!address && (h.joined || h.is_creator);
  const inviteUrl = h.invite_code ? `${window.location.origin}/hubs/${h.slug}?invite=${h.invite_code}` : "";

  return (
    <div className="pt-16 pb-20" data-testid="hub-page">
      <div className="h-48 sm:h-64 bg-[#241B15] relative">
        {h.banner && <img src={hubImageUrl(h.banner)} alt="" className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F101A] to-transparent" />
      </div>
      <div className="max-w-6xl mx-auto px-4 -mt-16 relative">
        <div className="flex flex-wrap items-end gap-4">
          <HubAvatar path={h.avatar} name={h.name} size="w-28 h-28" />
          <div className="flex-1 min-w-[220px]">
            <h1 className="font-display text-4xl sm:text-5xl font-black" data-testid="hub-name">{h.name}</h1>
            <p className="text-slate-300 mt-1">{h.tagline}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              <TierChip tier={h.tier} multiplier={h.multiplier} tid="hub-tier" />
              {h.tags.map((t) => <span key={t} className="chip">{t}</span>)}
              {h.status === "held" && <span className="chip border-amber-500/50 text-amber-300" data-testid="hub-under-review">Under review</span>}
            </div>
          </div>
          {h.is_creator
            ? <Link to={`/hubs/${h.slug}/edit`} className="btn-tactile btn-ghost flex items-center gap-2" data-testid="hub-edit-button"><Pencil size={14} /> Edit hub</Link>
            : <button onClick={toggleJoin} disabled={busy} className={`btn-tactile ${h.joined ? "btn-ghost" : "btn-flame"}`} data-testid="hub-join-button">{h.joined ? "Leave hub" : "Join Hub"}</button>}
        </div>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={Users} label="Members" value={h.member_count} tid="hub-stat-members" />
          <Stat icon={Activity} label="Active today" value={h.daily_active} tid="hub-stat-dau" />
          <Stat icon={Coins} label="FLAME earned" value={h.total_earned.toLocaleString()} tid="hub-stat-earned" />
          <Stat icon={Target} label="Next milestone" value={h.next_milestone ? `${h.eligible_members}/${h.next_milestone.members}` : "All reached"} tid="hub-stat-milestone" />
        </div>

        <div className="mt-8 grid lg:grid-cols-[1fr_320px] gap-8 items-start">
          <div className="space-y-4">
            <div className="flex gap-2">
              {["new", "top"].map((s) => <button key={s} onClick={() => setSort(s)} data-testid={`hub-posts-sort-${s}`} className={`chip ${sort === s ? "border-orange-500 text-orange-300" : ""}`}>{s === "new" ? "Newest" : "Top"}</button>)}
            </div>
            {canAct && <NewPost slug={h.slug} withSession={withSession} showToast={showToast} onPosted={() => load()} />}
            {h.posts.length === 0 && <div className="card-tile text-slate-500 text-sm" data-testid="hub-no-posts">No posts yet. {canAct ? "Start the first discussion." : "Join to start the conversation."}</div>}
            {h.posts.map((p) => <PostItem key={p.id} p={p} canAct={canAct} withSession={withSession} showToast={showToast} me={address} />)}
          </div>
          <aside className="space-y-4">
            {inviteUrl && (
              <div className="card-tile border-orange-500/40" data-testid="hub-invite-card">
                <div className="font-display font-bold">Your invite link</div>
                <p className="text-xs text-slate-400 mt-1">Earn 50 FLAME for each new player who joins through it and qualifies.</p>
                <div className="mt-2 flex items-center gap-2"><code className="text-xs break-all flex-1" data-testid="hub-invite-url">{inviteUrl}</code><CopyButton value={inviteUrl} tid="hub-invite-copy" label="invite link" /></div>
              </div>
            )}
            {h.rules && <div className="card-tile"><div className="font-display font-bold">Rules</div><p className="text-sm text-slate-300 mt-2 whitespace-pre-wrap" data-testid="hub-rules">{h.rules}</p></div>}
            {Object.keys(h.links).length > 0 && (
              <div className="card-tile flex flex-wrap gap-2" data-testid="hub-links">
                {Object.entries(h.links).map(([k, v]) => { const I = LINK_ICON[k]; return <a key={k} href={v} target="_blank" rel="noreferrer" className="chip hover:brightness-125" data-testid={`hub-link-${k}`}><I size={13} /> {k === "x" ? "X" : k[0].toUpperCase() + k.slice(1)}</a>; })}
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
